import React, { useEffect } from 'react';
import styled, { css } from 'styled-components';
import { THEMES } from 'components/Common/Accordion';

const ToggleGroup = styled.div`
  display: flex;
  gap: 4px;
  padding: 3px;
  background: ${({ invalid, theme }) =>
    invalid
      ? 'rgba(239, 68, 68, 0.08)'
      : theme?.gradientLight ||
        'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)'};
  border-radius: 10px;
  border: 2px solid ${({ invalid }) => (invalid ? '#ef4444' : 'transparent')};
  width: 100%;
  max-width: ${({ maxWidth }) => maxWidth || '280px'};
  transition: all 0.25s ease;
  box-shadow: ${({ invalid }) =>
    invalid ? '0 0 0 4px rgba(239, 68, 68, 0.15)' : 'none'};

  &:focus-within {
    box-shadow: ${({ invalid, theme }) =>
      invalid
        ? '0 0 0 4px rgba(239, 68, 68, 0.2)'
        : `0 0 0 4px ${theme?.shadowColor || 'rgba(102, 126, 234, 0.15)'}`};
    border-color: ${({ invalid }) => (invalid ? '#ef4444' : '#667eea')};
  }
`;

const ToggleOption = styled.button`
  flex: 1;
  padding: ${({ padding }) => padding || '6px 12px'};
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-weight: ${({ fontWeight }) => fontWeight || '500'};
  font-size: ${({ fontSize }) => fontSize || '12px'};
  transition: all 0.25s ease;
  background: ${({ active, theme }) =>
    active
      ? theme?.gradient || 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
      : 'transparent'};
  color: ${({ active, theme }) =>
    active ? 'white' : theme?.textColor || '#64748b'};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ gap }) => gap || '4px'};
  position: relative;

  &:hover {
    color: ${({ active, theme, disabled: isDisabled }) =>
      isDisabled
        ? theme?.textColor || '#64748b'
        : active
        ? 'white'
        : theme?.textColor || '#1e293b'};
    background: ${({ active, theme, disabled: isDisabled }) =>
      isDisabled
        ? 'transparent'
        : active
        ? theme?.gradient || 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
        : 'rgba(255,255,255,0.5)'};
  }

  ${({ disabled: isDisabled }) =>
    isDisabled &&
    css`
      opacity: 0.5;
      cursor: not-allowed;
      pointer-events: none;
      filter: grayscale(0.3);
    `}

  i {
    font-size: ${({ iconSize }) => iconSize || '14px'};
  }

  input {
    display: none;
  }
`;

const ErrorMessage = styled.div`
  color: #ef4444;
  font-size: 12px;
  font-weight: 500;
  margin-top: 4px;
  display: flex;
  align-items: center;
  gap: 4px;

  i {
    font-size: 14px;
  }
`;

const Wrapper = styled.div`
  width: 100%;
`;

export const ToggleSelector = ({
  options,
  value,
  onChange,
  disabled = false,
  maxWidth,
  padding,
  fontWeight,
  fontSize,
  gap,
  iconSize,
  className,
  required = true,
  invalid = false,
  errorMessage,
  showError = true,
  theme = 'secondary',
  autoSelectSingle = true,
  showDisabledTooltip = true,
  disabledTooltipText = 'This tab is disabled',
  ...rest
}) => {
  const themeConfig = THEMES[theme] || THEMES.secondary;

  // Auto-select the first option if there's only one option and no value is selected
  useEffect(() => {
    if (autoSelectSingle && options.length === 1 && !disabled) {
      const singleOption = options[0];
      const currentValue = value;

      const isEmpty =
        currentValue === null ||
        currentValue === undefined ||
        currentValue === '';
      if (isEmpty && singleOption && singleOption.value !== currentValue) {
        onChange(singleOption.value);
      }
    }
  }, [options, value, onChange, disabled, autoSelectSingle]);

  const isEmpty = value === null || value === undefined || value === '';
  const showInvalid = invalid || (required && isEmpty);
  const displayError = errorMessage || 'This field is required';

  // Check if all options are disabled
  const allDisabled = options.every(opt => opt.disabled);

  return (
    <Wrapper className={className}>
      <ToggleGroup
        maxWidth={maxWidth}
        invalid={showInvalid}
        theme={themeConfig}
        {...rest}
      >
        {options.map(option => {
          const isDisabled = disabled || option.disabled || allDisabled;
          const tooltipText = option.disabledTooltip || disabledTooltipText;

          return (
            <ToggleOption
              key={option.value}
              active={value === option.value}
              onClick={() => !isDisabled && onChange(option.value)}
              disabled={isDisabled}
              padding={padding}
              fontWeight={fontWeight}
              fontSize={fontSize}
              gap={gap}
              iconSize={iconSize}
              theme={themeConfig}
              title={isDisabled && showDisabledTooltip ? tooltipText : ''}
            >
              <input
                type="radio"
                checked={value === option.value}
                onChange={() => {}}
                disabled={isDisabled}
                required={required}
              />
              {option.icon && <i className={option.icon} />}
              <span>{option.label}</span>
            </ToggleOption>
          );
        })}
      </ToggleGroup>

      {showInvalid && showError && (
        <ErrorMessage>
          <i className="bx bx-error-circle" />
          {displayError}
        </ErrorMessage>
      )}
    </Wrapper>
  );
};

export default ToggleSelector;
